import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVerifyOtpMutation } from '../features/auth/authApi';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { setCredentials } from '../features/auth/authSlice';
import InlineError from '../components/InlineError';

/** Returns the default landing route based on user role */
/** Returns the default landing route based on user role */
function getHomeForRole(role: string): string {
    const r = (role || '').toLowerCase();
    switch (r) {
        case 'employee':
            return '/ess/dashboard';
        case 'admin':
        case 'super_admin':
        case 'hr_admin':
        case 'hr':
        case 'manager':
            return '/dashboard';
        default:
            return '/dashboard';
    }
}

export default function VerifyOtp() {
    const [otp, setOtp] = useState('');
    const [isSuccessPending, setIsSuccessPending] = useState(false);
    const [verifyOtp, { isLoading, error }] = useVerifyOtpMutation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const state = location.state as { email: string; tenant_slug: string; from?: string };
    const email = state?.email;
    const from = state?.from;

    useEffect(() => {
        if (!email) {
            navigate('/login');
        }
    }, [email, navigate]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (otp.length !== 6) return;

        try {
            const result = await verifyOtp({ 
                email, 
                otp_code: otp, 
                tenant_slug: state.tenant_slug 
            }).unwrap();

            if (!result.accessToken) {
                // Workspace is pending administrative approval
                setIsSuccessPending(true);
                return;
            }

            // Verification Log (Senior Architect Requirement)
            console.log('[AUTH_VERIFICATION] User:', JSON.stringify(result.user, null, 2));

            dispatch(setCredentials({ accessToken: result.accessToken!, user: result.user! }));

            // Role-based redirect: employees go to ESS, admins go to dashboard
            const role = result.user?.role || 'HR_ADMIN';
            const defaultHome = getHomeForRole(role);
            
            // Only use 'from' if it's safe for the user's role
            let destination = defaultHome;
            if (from) {
                if (role.toLowerCase() === 'employee' && !from.startsWith('/ess')) {
                    destination = defaultHome;
                } else {
                    destination = from;
                }
            }
            
            navigate(destination, { replace: true });
        } catch (err) {
            // Handled by InlineError
        }
    };

    if (isSuccessPending) {
        return (
            <div className="flex min-h-screen items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }}>
                <div className="w-full max-w-md text-center">
                    <div className="rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-3">Email Verified!</h1>
                        <p className="text-blue-200 text-sm leading-relaxed mb-6">
                            Your email is verified! Masirat is reviewing your application. We will email you once your workspace is ready.
                        </p>
                        <div className="pt-4 border-t border-white/10">
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-3 px-4 rounded-xl font-semibold text-white text-sm bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 transition-all duration-200"
                            >
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }}>
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-white">Check your email</h1>
                    <p className="mt-2 text-sm text-blue-200">
                        We sent a 6-digit code to{' '}
                        <span className="font-semibold text-white">{email}</span>
                    </p>
                </div>

                <div className="rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <form onSubmit={onSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="otp" className="block text-sm font-medium text-blue-200 text-center mb-4">
                                Verification Code
                            </label>
                            <input
                                id="otp"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                className="w-full text-center text-3xl tracking-[0.5em] font-mono py-4 px-4 rounded-xl border-0 outline-none focus:ring-2 focus:ring-blue-500"
                                style={{ background: 'rgba(255,255,255,0.08)', color: 'white', letterSpacing: '0.5em' }}
                                placeholder="••••••"
                                required
                                autoFocus
                            />
                        </div>

                        <InlineError message={(error as any)?.data?.error?.message || (error as any)?.data?.message || (error as any)?.error} />

                        <button
                            type="submit"
                            disabled={isLoading || otp.length !== 6}
                            className="w-full py-3 px-4 rounded-xl font-semibold text-white text-base transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ background: otp.length === 6 ? 'linear-gradient(135deg, #3b82f6, #6366f1)' : 'rgba(255,255,255,0.1)' }}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Verifying...
                                </span>
                            ) : 'Verify & Continue'}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-blue-300">
                        Didn't receive the code?{' '}
                        <button
                            onClick={() => navigate(-1)}
                            className="font-medium text-blue-400 hover:text-white transition-colors"
                        >
                            Go back
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
}
